import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  LayoutDashboard, 
  FileText, 
  BookOpen, 
  MessageSquare, 
  Users, 
  Euro,
  TrendingUp,
  Edit,
  Plus,
  ArrowRight,
  Loader2,
  Settings,
  Newspaper,
  Library,
  Radio,
  Gift,
  Mail,
  Megaphone,
  FileEdit,
  Flag,
  Tag,
  Code2,
  Crown,
  ShoppingCart,
  UserCog,
  Construction,
  ScrollText,
  BarChart3,
  FlaskConical,
  BookMarked,
  Sparkles,
  Images,
  ShoppingBag,
} from 'lucide-react';

function StatCard({ title, value, icon: Icon, color = "ruby", trend, link, onClick }) {
  const navigate = useNavigate();
  
  return (
    <Card 
      className={`bg-surface border-border cursor-pointer transition-all hover:border-${color}/50 hover:shadow-lg`}
      onClick={() => link ? navigate(link) : onClick?.()}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className={`text-3xl font-bold text-${color === 'ruby' ? 'white' : color}`}>{value}</p>
            {trend && (
              <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {trend}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-${color}/20`}>
            <Icon className={`w-6 h-6 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickAction({ title, description, icon: Icon, link, color = "ruby" }) {
  const navigate = useNavigate();
  
  return (
    <Card 
      className="bg-surface border-border cursor-pointer transition-all hover:border-ruby/50 group"
      onClick={() => navigate(link)}
    >
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`p-3 rounded-xl bg-${color}/20 shrink-0`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-foreground group-hover:text-ruby transition-colors">{title}</h3>
          <p className="text-sm text-muted-foreground truncate">{description}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-ruby transition-colors shrink-0" />
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadStats();
  }, [user, navigate]);

  const loadStats = async () => {
    try {
      const [
        storiesRes,
        seriesRes,
        libraryRes,
        cmsRes,
        subsRes
      ] = await Promise.all([
        api.get('/stories?limit=1'),
        api.get('/admin/series/stats'),
        api.get('/admin/library/stats'),
        api.get('/admin/cms/stats'),
        api.get('/admin/subscribers/stats').catch(() => ({ data: null }))
      ]);
      
      setStats({
        stories: storiesRes.data.length || 0,
        series: seriesRes.data,
        library: libraryRes.data,
        cms: cmsRes.data,
        subscribers: subsRes.data
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  // Premium revenue numbers come from successful WooCommerce activations.
  // The `series.total_revenue` and `library.total_revenue` were the legacy
  // per-item sales — kept in `Receita Total` for completeness but the focus
  // now is `valorPremium`.
  const valorPremium = stats?.cms?.premium_revenue?.gross ?? 0;
  const ganhosLeo = stats?.cms?.leo_earnings?.amount ?? 0;
  const totalRevenue = (stats?.series?.total_revenue || 0)
    + (stats?.library?.total_revenue || 0)
    + valorPremium;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-ruby to-ruby-light flex items-center justify-center">
              <LayoutDashboard className="w-7 h-7 text-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Painel de Administração</h1>
              <p className="text-muted-foreground">Gerir conteúdo e monitorizar a plataforma</p>
            </div>
          </div>
          <Badge className="bg-gold/20 text-gold border-gold/30 px-4 py-2">
            <Settings className="w-4 h-4 mr-2" />
            Administrador
          </Badge>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard 
            title="Total de Subscritores"
            value={stats?.subscribers?.total ?? '—'}
            icon={Users}
            color="blue-400"
            link="/admin/subscribers"
            trend={stats?.subscribers?.new_this_week ? `+${stats.subscribers.new_this_week} esta semana` : null}
          />
          <StatCard 
            title="Premium Ativos" 
            value={stats?.subscribers?.premium_active ?? '—'}
            icon={Crown}
            color="ruby"
            link="/admin/subscribers?status=premium"
          />
          <StatCard 
            title="Receita Total" 
            value={`€${totalRevenue.toFixed(2)}`}
            icon={Euro}
            color="green-400"
            link="/admin/woocommerce"
          />
          <StatCard 
            title="Valor Premium"
            value={`€${valorPremium.toFixed(2)}`}
            icon={Crown}
            color="gold"
            link="/admin/woocommerce"
            trend={
              stats?.cms?.premium_revenue
                ? `${stats.cms.premium_revenue.total_plans_sold} plano(s) • líq. €${stats.cms.premium_revenue.net.toFixed(2)}`
                : null
            }
          />
          <StatCard
            title="Ganhos Leo"
            value={`€${ganhosLeo.toFixed(2)}`}
            icon={TrendingUp}
            color="emerald-400"
            link="/admin/woocommerce"
            trend="40% do líquido (sem IVA)"
          />
          <StatCard 
            title="Blocos de Conteúdo" 
            value={stats?.cms?.total_content_blocks || 0}
            icon={FileEdit}
            color="purple-400"
            link="/admin/cms/content"
          />
        </div>

        {/* Content Management Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Quick Actions - Content */}
          <Card className="bg-surface border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Edit className="w-5 h-5 text-ruby" />
                Gestão de Conteúdo
              </CardTitle>
              <CardDescription>Editar textos e criar páginas</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <QuickAction 
                title="Editor de Conteúdo"
                description="Editar textos das páginas existentes"
                icon={FileEdit}
                link="/admin/cms/content"
                color="ruby"
              />
              <QuickAction 
                title="Página Premium"
                description="Editar planos, preços, FAQ e secções"
                icon={Crown}
                link="/admin/premium"
                color="gold"
              />
              <QuickAction 
                title="Compras WooCommerce"
                description="Webhooks e ativações Premium da pimentadocelingerie.pt"
                icon={ShoppingCart}
                link="/admin/woocommerce"
                color="ruby"
              />
              <QuickAction
                title="Analytics & Insights"
                description="Visitantes, conversão Premium e top conteúdo"
                icon={BarChart3}
                link="/admin/analytics"
                color="blue-400"
              />
              <QuickAction
                title="Livros"
                description="Catálogo privado com toggle de publicação"
                icon={BookMarked}
                link="/admin/livros"
                color="amber-400"
              />
              <QuickAction
                title="A/B Testing"
                description="Testa copy e badges para maximizar conversão"
                icon={FlaskConical}
                link="/admin/experiments"
                color="purple-400"
              />
              <QuickAction 
                title="Subscritores"
                description={stats?.subscribers ? `${stats.subscribers.total} utilizadores • ${stats.subscribers.premium_active} Premium ativos` : 'Gerir utilizadores'}
                icon={UserCog}
                link="/admin/subscribers"
                color="blue-400"
              />
              <QuickAction
                title="Aprovação de Contos"
                description="Rever histórias submetidas pela comunidade"
                icon={ScrollText}
                link="/admin/pending-stories"
                color="ruby"
              />
              <QuickAction 
                title="Modo de Manutenção"
                description="Fechar acesso público da plataforma com uma página de manutenção elegante"
                icon={Construction}
                link="/admin/maintenance"
                color="gold"
              />
              <QuickAction 
                title="Criar Nova Página"
                description="Criar páginas personalizadas"
                icon={Plus}
                link="/admin/cms/pages"
                color="blue-400"
              />
              <QuickAction 
                title="Gerir Páginas"
                description={`${stats?.cms?.total_custom_pages || 0} páginas criadas`}
                icon={Newspaper}
                link="/admin/cms/pages"
                color="green-400"
              />
            </CardContent>
          </Card>

          {/* Quick Actions - Products */}
          <Card className="bg-surface border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Library className="w-5 h-5 text-ruby" />
                Gestão de Produtos
              </CardTitle>
              <CardDescription>Histórias, séries e infoprodutos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <QuickAction 
                title="Publicar História"
                description="Carregar e publicar novas histórias"
                icon={BookOpen}
                link="/admin/cms/stories"
                color="ruby"
              />
              <QuickAction 
                title="Gerir Séries"
                description={`${stats?.series?.total_series || 0} séries • €${stats?.series?.total_revenue?.toFixed(2) || '0.00'} receita`}
                icon={BookOpen}
                link="/admin/series"
                color="blue-400"
              />
              <QuickAction 
                title="Gerir Infoprodutos"
                description={`${stats?.library?.total_products || 0} produtos • €${stats?.library?.total_revenue?.toFixed(2) || '0.00'} receita`}
                icon={FileText}
                link="/admin/library"
                color="green-400"
              />
            </CardContent>
          </Card>
        </div>

        {/* More Admin Features */}
        <Card className="bg-surface border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Settings className="w-5 h-5 text-ruby" />
              Mais Funcionalidades
            </CardTitle>
            <CardDescription>Outras áreas de administração</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <QuickAction 
                title="Moderação de Comentários"
                description="Aprovar/rejeitar comentários"
                icon={MessageSquare}
                link="/admin/comments"
                color="ruby"
              />
              <QuickAction 
                title="Relatórios de Conteúdo"
                description="Gerir conteúdo reportado"
                icon={Flag}
                link="/admin/reports"
                color="red-500"
              />
              <QuickAction 
                title="Moderação de Confissões"
                description="Aprovar/rejeitar confissões"
                icon={MessageSquare}
                link="/admin/confessions"
                color="gold"
              />
              <QuickAction 
                title="Categorias de Contos"
                description="Criar e gerir categorias"
                icon={Tag}
                link="/admin/categories"
                color="ruby"
              />
              <QuickAction 
                title="Categorias de Confissões"
                description="Gerir categorias de confissões"
                icon={MessageSquare}
                link="/admin/confession-categories"
                color="amber-400"
              />
              <QuickAction 
                title="Categorias de + Prazer"
                description="Gerir categorias dos artigos"
                icon={Tag}
                link="/admin/article-categories"
                color="ruby"
              />
              <QuickAction 
                title="Artigos Educativos"
                description="Secção + Prazer"
                icon={Newspaper}
                link="/admin/cms/articles"
                color="blue-400"
              />
              <QuickAction 
                title="Gestão de Canais"
                description="Criar e gerir canais"
                icon={Radio}
                link="/admin/channels"
                color="green-400"
              />
              <QuickAction 
                title="Gestão de Pontos"
                description="Configurar recompensas"
                icon={Gift}
                link="/admin/points"
                color="ruby"
              />
              <QuickAction 
                title="Enviar Mensagens"
                description="Comunicar com utilizadores"
                icon={Mail}
                link="/admin/messages"
                color="blue-400"
              />
              <QuickAction
                title="Gestão da Loja"
                description="Produtos dos parceiros em /loja"
                icon={ShoppingBag}
                link="/admin/shop"
                color="ruby"
              />
              <QuickAction 
                title="Gerir Anúncios"
                description="Publicidade da plataforma"
                icon={Megaphone}
                link="/admin/ads"
                color="gold"
              />
              <QuickAction
                title="Embeds para WordPress"
                description="Iframe do slide dos contos"
                icon={Code2}
                link="/admin/embeds"
                color="ruby"
              />
              <QuickAction
                title="Hero da Homepage"
                description="Série em destaque, texto, imagem e datas"
                icon={Sparkles}
                link="/admin/homepage-hero"
                color="ruby"
              />
              <QuickAction
                title="Biblioteca de Media"
                description="Gerir imagens em Bunny.net e substituir capas em falta"
                icon={Images}
                link="/admin/media"
                color="blue-400"
              />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
